<template>
  <div
    class="main-menu menu-fixed menu-accordion menu-shadow"
    :class="[
      { 'expanded': !isVerticalMenuCollapsed || (isVerticalMenuCollapsed && isMouseHovered) },
      skin === 'semi-dark' ? 'menu-dark' : 'menu-light',
      isBudgetMode ? 'prep-budget-menu' : null
    ]"
    @mouseenter="updateMouseHovered(true)"
    @mouseleave="updateMouseHovered(false)"
  >
    <!-- main menu header-->
    <div class="navbar-header expanded">
      <slot
        name="header"
        :toggleVerticalMenuActive="toggleVerticalMenuActive"
        :toggleCollapsed="toggleCollapsed"
        :collapseTogglerIcon="collapseTogglerIcon"
      >
        <ul class="nav navbar-nav flex-row">

          <!-- Logo & Text -->
          <li
            class="nav-item mr-auto"
            style="width: 100%;"
          >
            <b-link
              class="navbar-brand"
              to="/"
              style="width: 100%;"
              @click="handleBrandClick"
            >
              <span
                style="width: 100%;"
                class="brand-logo"
              >
                <b-img
                  :src="appLogoImage"
                  alt="logo"
                />
              </span>
            </b-link>
          </li>

          <!-- Toggler Button -->
          <!--      <li class="nav-item nav-toggle">
            <b-link class="nav-link modern-nav-toggle">
              <feather-icon
                icon="XIcon"
                size="20"
                class="d-block d-xl-none"
                @click="toggleVerticalMenuActive"
              />
              <feather-icon
                :icon="collapseTogglerIconFeather"
                size="20"
                class="d-none d-xl-block collapse-toggle-icon"
                @click="toggleCollapsed"
              />
            </b-link>
          </li>-->
        </ul>
      </slot>
    </div>
    <!-- / main menu header-->

    <!-- Shadow -->
    <div
      :class="{'d-block': shallShadowBottom}"
      class="shadow-bottom"
    />

    <!-- main menu content-->
    <vue-perfect-scrollbar
      ref="refMenuScroll"
      :settings="perfectScrollbarSettings"
      class="main-menu-content scroll-area"
      :class="{ 'prep-budget-scroll': isBudgetMode }"
      :tagname="isBudgetMode ? 'div' : 'ul'"
      @ps-scroll-y="evt => { shallShadowBottom = evt.srcElement.scrollTop > 0 }"
    >
      <template v-if="!isBudgetMode">
        <vertical-nav-menu-items
          :items="menuItems"
          class="navigation navigation-main"
        />
      </template>
      <template v-else>
        <vertical-nav-menu-items
          :items="menuItems"
          class="navigation navigation-main prep-budget-nav prep-budget-nav-top"
        />
        <vertical-nav-menu-items
          :items="footerItems"
          class="navigation navigation-main prep-budget-nav prep-budget-nav-bottom"
        />
      </template>
    </vue-perfect-scrollbar>
    <!-- /main menu content-->
  </div>
</template>

<script>
import axios from 'axios'
import navMenuItems from '@/navigation/vertical'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { BLink, BImg } from 'bootstrap-vue'
import {
  provide, computed, ref, getCurrentInstance, watch, nextTick, onMounted, onBeforeUnmount,
} from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import { $themeConfig } from '@themeConfig'
import VerticalNavMenuItems from './components/vertical-nav-menu-items/VerticalNavMenuItems.vue'
import useVerticalNavMenu from './useVerticalNavMenu'

export default {
  components: {
    VuePerfectScrollbar,
    VerticalNavMenuItems,
    BLink,
    BImg,
  },
  props: {
    isVerticalMenuActive: {
      type: Boolean,
      required: true,
    },
    toggleVerticalMenuActive: {
      type: Function,
      required: true,
    },
  },
  setup(props) {
    const instance = getCurrentInstance()
    const store = instance && instance.proxy ? instance.proxy.$store : null
    const router = instance && instance.proxy ? instance.proxy.$router : null
    const {
      isMouseHovered,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      updateMouseHovered,
    } = useVerticalNavMenu(props)

    const { skin } = useAppConfig()

    // Shadow bottom is UI specific and can be removed by user => It's not in `useVerticalNavMenu`
    const shallShadowBottom = ref(false)
    const refMenuScroll = ref(null)

    provide('isMouseHovered', isMouseHovered)

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const refreshMenuScroll = () => {
      nextTick(() => {
        const menuScroll = refMenuScroll.value

        if (menuScroll && typeof menuScroll.update === 'function') {
          menuScroll.update()
        }
      })
    }

    const collapseTogglerIconFeather = computed(() => (collapseTogglerIcon.value === 'unpinned' ? 'CircleIcon' : 'DiscIcon'))

    // App Name
    const { appName, appLogoImage } = $themeConfig.app

    const isBudgetMode = computed(() => (store && store.state && store.state.app && store.state.app.prepBudgetMode) || false)
    const prepBudgetDynamicLevels = computed(() => {
      if (!store || !store.state || !store.state.app) return []
      return store.state.app.prepBudgetDynamicLevels || []
    })
    const prepBudgetCustomMenus = computed(() => {
      if (!store || !store.state || !store.state.app) return []
      return store.state.app.prepBudgetCustomMenus || []
    })
    const prepBudgetActiveMenuKey = computed(() => {
      if (!store || !store.state || !store.state.app) return ''
      return store.state.app.prepBudgetActiveMenuKey || ''
    })
    const prepBudgetOfferType = computed(() => {
      if (!store || !store.state || !store.state.app) return ''
      return store.state.app.prepBudgetOfferType || ''
    })
    const prepBudgetOfferId = computed(() => {
      if (!store || !store.state || !store.state.app) return null
      return store.state.app.prepBudgetOfferId || null
    })
    const prepBudgetFillMode = computed(() => {
      if (!store || !store.state || !store.state.app) return false
      return !!store.state.app.prepBudgetFillMode
    })
    const collapsedParentMenus = ref({})

    const displayTitle = menu => {
      const key = menu && menu.key ? String(menu.key) : ''
      const title = menu && menu.title ? String(menu.title) : ''
      if (key === 'zsaluzas-allvanyozas') return 'Zsalu, állvány'
      if (title.toLowerCase().includes('zsaluzás') && title.toLowerCase().includes('állvány')) return 'Zsalu, állvány'
      return title
    }
    const deleteCustomMenu = async menu => {
      if (!store || !menu || !menu.key) return false
      const title = menu.title || 'elem'
      const confirmed = window.confirm(`Biztosan törölni akarod ezt a menüt?\n\n${title}`)
      if (!confirmed) return false

      const offerType = String(prepBudgetOfferType.value || '')
      const offerId = Number(prepBudgetOfferId.value || 0)
      if (!offerType || !offerId) return false

      try {
        const token = localStorage.getItem('token')
        const response = await axios({
          method: 'post',
          url: 'budget/offer/menu-config/remove',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data: {
            offer_type: offerType,
            offer_id: offerId,
            menu_key: menu.key,
          },
        })
        const menus = (response.data && Array.isArray(response.data.custom_menus)) ? response.data.custom_menus : []
        const fallbackKey = menu.parentKey || ''
        store.commit('app/SET_PREP_BUDGET_CUSTOM_MENUS', menus)
        if (fallbackKey && menus.some(item => String(item.key) === String(fallbackKey))) {
          store.commit('app/SET_PREP_BUDGET_ACTIVE_MENU', fallbackKey)
        } else {
          store.commit('app/SET_PREP_BUDGET_ACTIVE_MENU', '')
        }
      } catch (e) {
        // hibánál marad a jelenlegi állapot
      }
      return false
    }
    const buildItem = (title, key, icon = 'LayersIcon') => {
      const isActive = prepBudgetActiveMenuKey.value === key
      return {
        title,
        key,
        icon,
        roles: ['prep_view_all', 'prep_assign_project', 'prep_view_own'],
        href: '#',
        active: isActive,
        itemClass: isActive ? 'prep-budget-active-item' : '',
        linkClass: isActive ? 'prep-budget-active-link' : '',
        titleClass: isActive ? 'prep-budget-active-title' : '',
        onClick: () => {
          if (!store) return false
          store.commit('app/SET_PREP_BUDGET_ACTIVE_MENU', key)
          return false
        },
      }
    }
    const triggerProjectPriceImport = () => {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('prep-open-project-price-import'))
      }
      return false
    }
    const triggerCorrection = () => {
      if (!store) return false
      store.commit('app/SET_PREP_BUDGET_ACTIVE_MENU', 'korrekcio')
      return false
    }

    const goToPreparationList = () => {
      if (!store) return false
      const backRoute = store.state && store.state.app ? store.state.app.prepBudgetBackRoute : null
      if (router && backRoute) {
        router.push(backRoute)
      } else if (router) {
        router.push({ name: 'monolit.preparation.list' })
      }
      store.commit('app/SET_PREP_BUDGET_MODE', false)
      store.commit('app/SET_PREP_BUDGET_TITLE', '')
      store.commit('app/SET_PREP_BUDGET_ACTIVE_MENU', '')
      store.commit('app/SET_PREP_BUDGET_BACK_ROUTE', null)
      store.commit('app/SET_PREP_BUDGET_CONTEXT', { offerType: '', offerId: null })
      store.commit('app/SET_PREP_BUDGET_MENU_SET', 'default')
      store.commit('app/CLOSE_PREP_BUDGET_ADD_MODAL')
      store.commit('app/RESET_PREP_BUDGET_CUSTOM_MENUS')
      return false
    }

    const backFromBudget = () => goToPreparationList()

    const handleBrandClick = () => {
      if (!store) return
      store.commit('app/SET_PREP_BUDGET_MODE', false)
      store.commit('app/SET_PREP_BUDGET_TITLE', '')
      store.commit('app/SET_PREP_BUDGET_ACTIVE_MENU', '')
      store.commit('app/SET_PREP_BUDGET_CONTEXT', { offerType: '', offerId: null })
      store.commit('app/SET_PREP_BUDGET_MENU_SET', 'default')
      store.commit('app/CLOSE_PREP_BUDGET_ADD_MODAL')
    }

    const footerItems = computed(() => ([
      {
        title: 'Projekt ár importálás',
        icon: 'LayersIcon',
        roles: ['prep_view_all', 'prep_assign_project', 'prep_view_own'],
        href: '#',
        onClick: triggerProjectPriceImport,
      },
      {
        title: 'Korrekció',
        icon: 'LayersIcon',
        roles: ['prep_view_all', 'prep_assign_project', 'prep_view_own'],
        href: '#',
        onClick: triggerCorrection,
      },
      {
        title: 'Vissza',
        icon: 'CornerUpLeftIcon',
        roles: ['prep_view_all', 'prep_assign_project', 'prep_view_own'],
        href: '#',
        onClick: backFromBudget,
      },
    ]))
    const menuItems = computed(() => {
      if (!isBudgetMode.value) return navMenuItems
      const customMenus = Array.isArray(prepBudgetCustomMenus.value) ? prepBudgetCustomMenus.value : []
      const menuByKey = customMenus.reduce((acc, menu) => {
        if (menu && menu.key) acc[menu.key] = menu
        return acc
      }, {})
      const rootParentKeyOf = menu => {
        if (!menu) return null
        let current = menu
        let guard = 0
        while (current && current.parentKey && guard < 20) {
          const next = menuByKey[current.parentKey]
          if (!next) break
          current = next
          guard += 1
        }
        return current && current.key ? current.key : (menu.parentKey || null)
      }
      const effectiveParentKey = menu => {
        if (!menu || !menu.parentKey) return null
        const parent = menuByKey[menu.parentKey]
        if (!parent) return menu.parentKey
        if (!parent.parentKey) return parent.key
        return rootParentKeyOf(parent)
      }
      const hasChildMenus = key => customMenus.some(menu => effectiveParentKey(menu) === key)
      const activeRootKey = computed(() => {
        const activeKey = prepBudgetActiveMenuKey.value
        if (!activeKey) return ''
        const activeMenu = menuByKey[activeKey]
        if (!activeMenu) return activeKey
        return rootParentKeyOf(activeMenu) || activeKey
      })
      const buildCustomBranch = (parentKey = null, depth = 0) => {
        const nodes = customMenus.filter(menu => (menu && effectiveParentKey(menu) === parentKey))
        return nodes.flatMap(menu => {
          const key = menu && menu.key ? menu.key : ''
          if (!key) return []
          const depthClass = depth > 0 ? `prep-submenu-level-${Math.min(depth, 4)}` : ''
          const shortTitle = displayTitle(menu)
          const node = buildItem(shortTitle, key)
          node.deletable = true
          node.onDeleteClick = () => deleteCustomMenu(menu)
          const isParentNode = hasChildMenus(key)
          const isExpanded = collapsedParentMenus.value[key] !== true
          const isSikalapozasParent = !menu.parentKey && !menu.subgroupKey && menu.categoryKey === 'sikalapozas'
          const isDirectItemsParent = !menu.parentKey && !menu.subgroupKey && menu.childMode === 'direct_items'

          if (isParentNode) {
            node.title = `${isExpanded ? '▾' : '▸'} ${shortTitle}`
            node.onClick = () => {
              if (!store) return false
              collapsedParentMenus.value = {
                ...collapsedParentMenus.value,
                [key]: isExpanded,
              }
              store.commit('app/SET_PREP_BUDGET_ACTIVE_MENU', key)
              return false
            }
          }
          if (depthClass) {
            node.itemClass = depthClass
            node.linkClass = depthClass
            node.titleClass = depthClass
          }

          const children = isParentNode && !isExpanded ? [] : buildCustomBranch(key, depth + 1)
          const items = [node, ...children]
          if (prepBudgetFillMode.value && depth === 0 && activeRootKey.value === key && (!isParentNode || isExpanded) && !isSikalapozasParent && !isDirectItemsParent) {
            items.push({
              title: 'Add',
              icon: 'PlusCircleIcon',
              itemClass: `prep-submenu-add-item ${depthClass}`.trim(),
              linkClass: `prep-submenu-add-link ${depthClass}`.trim(),
              titleClass: 'prep-submenu-add-title',
              roles: ['prep_view_all', 'prep_assign_project', 'prep_view_own'],
              href: '#',
              onClick: () => {
                if (!store) return false
                store.commit('app/OPEN_PREP_BUDGET_ADD_MODAL', 'sub')
                return false
              },
            })
          }
          return items
        })
      }

      const customItems = buildCustomBranch(null, 0)
      const customMenuKeys = new Set(customItems.map(item => item && item.key).filter(Boolean))
      const buildDefaultItem = (title, key, icon = 'LayersIcon') => {
        if (customMenuKeys.has(key)) return null
        return buildItem(title, key, icon)
      }
      const defaultBudgetItems = [
        buildDefaultItem('Főösszesítő', 'foosszesito'),
        buildDefaultItem('Összesítő', 'osszesito'),
      ].filter(Boolean)
      return [
        ...defaultBudgetItems,
        ...customItems,
        ...(prepBudgetFillMode.value ? [{
          title: 'Add',
          icon: 'PlusCircleIcon',
          roles: ['prep_view_all', 'prep_assign_project', 'prep_view_own'],
          href: '#',
          onClick: () => {
            if (!store) return false
            store.commit('app/OPEN_PREP_BUDGET_ADD_MODAL', 'main')
            return false
          },
        }] : []),
      ]
    })

    watch(
      [
        menuItems,
        isBudgetMode,
        isVerticalMenuCollapsed,
        isMouseHovered,
        () => (instance && instance.proxy && instance.proxy.$route ? instance.proxy.$route.fullPath : ''),
      ],
      refreshMenuScroll,
      { deep: true },
    )

    onMounted(() => {
      refreshMenuScroll()
      window.addEventListener('resize', refreshMenuScroll)
      setTimeout(refreshMenuScroll, 150)
      setTimeout(refreshMenuScroll, 500)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', refreshMenuScroll)
    })

    return {
      navMenuItems,
      menuItems,
      isBudgetMode,
      refMenuScroll,
      perfectScrollbarSettings,
      isVerticalMenuCollapsed,
      collapseTogglerIcon,
      toggleCollapsed,
      isMouseHovered,
      updateMouseHovered,
      collapseTogglerIconFeather,

      // Shadow Bottom
      shallShadowBottom,

      // Skin
      skin,

      // App Name
      appName,
      appLogoImage,

      footerItems,
      prepBudgetOfferType,
      prepBudgetOfferId,
      prepBudgetDynamicLevels,
      handleBrandClick,
    }
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/vertical-menu.scss";

.main-menu.prep-budget-menu {
  display: flex;
  flex-direction: column;
  background:
    radial-gradient(760px 340px at 8% 0%, rgba(236, 72, 153, 0.22) 0%, transparent 62%),
    radial-gradient(620px 340px at 100% 0%, rgba(96, 165, 250, 0.2) 0%, transparent 67%),
    linear-gradient(180deg, rgba(28, 37, 67, 0.98) 0%, rgba(18, 26, 51, 0.99) 100%) !important;
  border-right: 1px solid rgba(96, 165, 250, 0.22);
  box-shadow:
    inset -1px 0 0 rgba(167, 139, 250, 0.14),
    inset 0 1px 0 rgba(236, 72, 153, 0.16);
}

.main-menu.prep-budget-menu .navbar-header {
  background: rgba(255, 255, 255, 0.01);
  border-bottom: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: inset 0 -1px 0 rgba(96, 165, 250, 0.18);
}

.main-menu.prep-budget-menu .main-menu-content {
  background: transparent;
}

.prep-budget-menu .main-menu-content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.main-menu:not(.prep-budget-menu) .main-menu-content {
  overflow-y: auto;
  overflow-x: hidden;
}

.prep-budget-scroll {
  padding-bottom: 0;
}

.prep-budget-nav {
  background: transparent;
}

.main-menu.prep-budget-menu .navigation > li > a {
  border-radius: 8px;
  margin: 2px 8px;
  border: 0;
  background: transparent;
  box-shadow: none;
  transition: background-color 140ms ease, color 140ms ease;
}

.main-menu.prep-budget-menu .navigation > li > a:hover {
  background: rgba(148, 163, 184, 0.12);
  box-shadow: none;
}

.main-menu.prep-budget-menu .navigation > li.active > a {
  background: rgba(96, 165, 250, 0.18) !important;
  box-shadow: none;
}

/* Neon icon palette for preparation budget menu */
.main-menu.prep-budget-menu .navigation > li > a {
  --prep-icon-neon: #7dd3fc;
}

.main-menu.prep-budget-menu .navigation > li:nth-child(6n + 1) > a { --prep-icon-neon: #7dd3fc; }
.main-menu.prep-budget-menu .navigation > li:nth-child(6n + 2) > a { --prep-icon-neon: #a78bfa; }
.main-menu.prep-budget-menu .navigation > li:nth-child(6n + 3) > a { --prep-icon-neon: #f472b6; }
.main-menu.prep-budget-menu .navigation > li:nth-child(6n + 4) > a { --prep-icon-neon: #f59e0b; }
.main-menu.prep-budget-menu .navigation > li:nth-child(6n + 5) > a { --prep-icon-neon: #34d399; }
.main-menu.prep-budget-menu .navigation > li:nth-child(6n + 6) > a { --prep-icon-neon: #60a5fa; }

.main-menu.prep-budget-menu .navigation > li > a > svg:first-of-type,
.main-menu.prep-budget-menu .navigation > li > a > .feather-icon:first-of-type {
  color: var(--prep-icon-neon) !important;
  filter:
    drop-shadow(0 0 2px color-mix(in srgb, var(--prep-icon-neon) 70%, transparent 30%))
    drop-shadow(0 0 8px color-mix(in srgb, var(--prep-icon-neon) 55%, transparent 45%));
  opacity: 1 !important;
}

.main-menu.prep-budget-menu .navigation > li.active > a > svg:first-of-type,
.main-menu.prep-budget-menu .navigation > li.active > a > .feather-icon:first-of-type {
  filter:
    drop-shadow(0 0 3px color-mix(in srgb, var(--prep-icon-neon) 80%, transparent 20%))
    drop-shadow(0 0 12px color-mix(in srgb, var(--prep-icon-neon) 65%, transparent 35%));
}

/* Apply neon icons to the regular left main menu as well */
.vertical-layout .main-menu .navigation > li > a {
  --menu-icon-neon: #7dd3fc;
}

.vertical-layout .main-menu .navigation > li:nth-child(6n + 1) > a { --menu-icon-neon: #7dd3fc; }
.vertical-layout .main-menu .navigation > li:nth-child(6n + 2) > a { --menu-icon-neon: #a78bfa; }
.vertical-layout .main-menu .navigation > li:nth-child(6n + 3) > a { --menu-icon-neon: #f472b6; }
.vertical-layout .main-menu .navigation > li:nth-child(6n + 4) > a { --menu-icon-neon: #f59e0b; }
.vertical-layout .main-menu .navigation > li:nth-child(6n + 5) > a { --menu-icon-neon: #34d399; }
.vertical-layout .main-menu .navigation > li:nth-child(6n + 6) > a { --menu-icon-neon: #60a5fa; }

.vertical-layout .main-menu .navigation > li > a > svg:first-of-type,
.vertical-layout .main-menu .navigation > li > a > .feather-icon:first-of-type {
  color: var(--menu-icon-neon) !important;
  filter:
    drop-shadow(0 0 2px color-mix(in srgb, var(--menu-icon-neon) 70%, transparent 30%))
    drop-shadow(0 0 8px color-mix(in srgb, var(--menu-icon-neon) 55%, transparent 45%));
  opacity: 1 !important;
}

.vertical-layout .main-menu .navigation > li.active > a > svg:first-of-type,
.vertical-layout .main-menu .navigation > li.active > a > .feather-icon:first-of-type {
  filter:
    drop-shadow(0 0 3px color-mix(in srgb, var(--menu-icon-neon) 80%, transparent 20%))
    drop-shadow(0 0 12px color-mix(in srgb, var(--menu-icon-neon) 65%, transparent 35%));
}

.prep-budget-nav-top {
  flex: 1 1 auto;
  overflow: auto;
  padding-bottom: 0;
}

.main-menu.prep-budget-menu .prep-budget-nav-bottom {
  flex: 0 0 auto;
  margin-top: auto;
  padding-bottom: 0.75rem;
  border-top: 1px solid rgba(148, 163, 184, 0.08);
  padding-top: 0.5rem;
}

.prep-budget-nav-bottom .nav-item > a,
.prep-budget-nav-bottom .nav-item > a .menu-title,
.prep-budget-nav-bottom .nav-item > a svg {
  color: inherit;
  opacity: 1;
}

.prep-budget-nav-bottom .nav-item > a {
  font-weight: inherit;
}

.prep-budget-menu .prep-submenu-add-item > a.prep-submenu-add-link {
  padding-left: 2.5rem !important;
}

.prep-budget-menu .prep-submenu-add-item > a.prep-submenu-add-link .prep-submenu-add-title {
  font-size: 0.86rem;
  opacity: 0.92;
}

.prep-budget-menu .nav-item > a.prep-submenu-level-1 {
  padding-left: 2.35rem !important;
}

.prep-budget-menu .nav-item > a.prep-submenu-level-1 .menu-title {
  font-size: 0.92rem;
}

.prep-budget-menu .nav-item > a.prep-submenu-level-2 {
  padding-left: 2.95rem !important;
}

.prep-budget-menu .nav-item > a.prep-submenu-level-2 .menu-title {
  font-size: 0.88rem;
}

.prep-budget-menu .nav-item.prep-budget-active-item {
  position: relative;
}

.prep-budget-menu .nav-item > a.prep-budget-active-link {
  background: linear-gradient(90deg, rgba(78, 183, 255, 0.22) 0%, rgba(78, 183, 255, 0.08) 100%) !important;
  box-shadow: inset 3px 0 0 #4eb7ff, 0 0 0 1px rgba(78, 183, 255, 0.18) !important;
  border-radius: 10px !important;
}

.prep-budget-menu .nav-item > a.prep-budget-active-link .prep-budget-active-title,
.prep-budget-menu .nav-item > a.prep-budget-active-link .menu-title {
  color: #dff4ff !important;
  font-weight: 600 !important;
}

.prep-budget-menu .nav-item > a.prep-budget-active-link svg,
.prep-budget-menu .nav-item > a.prep-budget-active-link .feather {
  color: #7fd3ff !important;
}

/* Remove blue active highlight from vertical menu items */
.vertical-layout .main-menu .navigation > li.active > a,
.vertical-layout .main-menu .navigation > li > ul li.active,
.vertical-layout .main-menu .navigation > li > ul li.active > a {
  background: transparent !important;
  box-shadow: none !important;
  color: inherit !important;
  font-weight: 400 !important;
}
</style>
